import type { Map } from "./types";
import { estMeters } from "./units";

export const PRICE_PER_TILE = 15.00;
export const PRICE_PER_FRAME_SEGMENT = 2.00;
export const PRICE_PER_VENEER = 1.50;
export const PRICE_HANGERS = 2.25;

export const PRINT_TIME_TILE = 14 * 3600;
export const PRINT_TIME_FRAME = 0.52 * 3600;

// Piece enumeration keys on the .obj (the canonical print/download artifact).
// Each piece also ships a .glb sibling for the viewer (see render_job.py); the
// carousel resolves that from the .obj key, so listing only .obj here keeps one
// entry per physical piece — downloads, tile cells, and counts stay correct.
export function getFramePieces(parts: { [key: string]: string }) {
    let rv: string[] = [];
    for (const k of Object.keys(parts)) {
        if (k.includes("frame") && k.endsWith(".obj")) rv.push(k);
    }
    rv.sort();
    return rv;
}

export function getRenderPieces(parts: { [key: string]: string }) {
    let rv: string[] = [];
    for (const k of Object.keys(parts)) {
        if (k.includes("render") && k.endsWith(".obj")) rv.push(k);
    }
    rv.sort();
    return rv;
}

// viewerModel resolves the piece the 3D viewer should load for a given .obj
// key: the smaller, colour-carrying .glb sibling when the render pipeline
// produced one, else the .obj itself (older maps predating glTF output). Also
// reports which format so the viewer can pick the right three.js loader.
export function viewerModel(
    files: { [key: string]: string },
    objKey: string,
): { url: string; format: "glb" | "obj" } {
    const glbKey = objKey.replace(/\.obj$/, ".glb");
    if (glbKey !== objKey && files[glbKey]) {
        return { url: files[glbKey], format: "glb" };
    }
    return { url: files[objKey], format: "obj" };
}

export function getPrice(map: Map): number {
    return (estimateFramePieces(map) * PRICE_PER_FRAME_SEGMENT)
        + (estimateRenderPieces(map) * PRICE_PER_TILE)
        + (8 * PRICE_PER_VENEER)
        + PRICE_HANGERS + (map.status === "framed" ? -10 : 0);
}

function whXY(map: Map) {
    const [realW, realH] = estMeters(map.extent.ll, map.extent.ur);
    if (realW === 0) return [0, 0, 0, 0];
    const height = (map.width * realH) / realW;
    const nTilesW = Math.ceil(map.width / map.maxX);
    const nTilesH = Math.ceil(height / map.maxX);
    return [map.width, height, nTilesW, nTilesH];
}

export function estimateFramePieces(map: Map): number {
    const [, , x, y] = whXY(map);
    return 2 * (x + y);
}

export function estimateRenderPieces(map: Map): number {
    const [, , x, y] = whXY(map);
    return x * y;
}

export function leadTime(map: Map): number {
    return estimateFramePieces(map) * PRINT_TIME_FRAME
        + estimateRenderPieces(map) * PRINT_TIME_TILE;
}

// ---------------------------------------------------------------- //
// Coupons. Mirrors backend/pricing.go couponDiscountCents — the server
// is authoritative at order time; this is the checkout preview.

import type { Discount } from "./types";

// Stripe's USD minimum charge; a remainder under it is comped.
export const MIN_CHARGE_CENTS = 50;

export function discountCentsFor(d: Discount, subtotalCents: number): number {
    let off = 0;
    if (d.free) {
        off = subtotalCents;
    } else if (d.percentOff && d.percentOff > 0) {
        off = Math.round((subtotalCents * d.percentOff) / 100);
    } else if (d.creditCents) {
        off = d.creditCents;
    }
    off = Math.max(0, Math.min(off, subtotalCents));
    if (subtotalCents - off < MIN_CHARGE_CENTS) {
        off = subtotalCents;
    }
    return off;
}

export function discountLabel(d: Discount): string {
    if (d.free) return "free order";
    if (d.percentOff && d.percentOff > 0) return `${d.percentOff}% off`;
    if (d.creditCents) return `$${(d.creditCents / 100).toFixed(2)} off`;
    return "";
}
